// 跨案場分析的下載組表。
//
// 為什麼跟圖表分開走一條路：圖表是「寬」的（一條線 = 一個案場的一個指標，
// 沿著時間軸鋪開），下載是「長」的（一列 = 一個案場的一個時間點，四個指標同列）。
// 原本兩者共用一份跟 series 平行維護的 downloadArrayForExcel，補 null 的格數
// 還跟 categories 差一格，缺資料的案場照樣佔一列空白。
// 改成直接從後端回的 series 攤平，時間格對齊與空案場過濾都變成不用維護的事。
//
// 形狀改成長表的理由見 docs/adr/0010-cross-site-download-long-format.md

/** 四個指標的欄位 key 與顯示名稱。圖表的開關選項也是從這裡長出來的，只有這一份 */
const METRICS = [
  { key: 'pr', label: 'PR值' },
  { key: 'ENVTemp', label: '環境溫度' },
  { key: 'IRR', label: '等效日照時數(h)' },
  { key: 'acp', label: '峰瓩值(kWh/kWp)' }
]

/** 圖表開關用的字串格式 `顯示名稱-欄位key`，label 裡不能有 '-' */
const metricOption = (m) => `${m.label}-${m.key}`

const GRAIN_LABEL = { hour: '時', date: '日', month: '月', year: '年' }

/**
 * 讓試算表把時間**原樣**顯示。
 *
 * `2026-09`、`2026-09-01`、`03:00` 丟進 Excel 都會被當成日期／時間吃掉，
 * 分別變成 `Sep-26`、`2026/9/1`、`上午 03:00:00` —— 跟我們寫進去的值對不起來，
 * 而且不同粒度被改成不同樣子，同一欄看起來像三種東西。
 *
 * `="..."` 是試算表通用的「這格是文字」寫法，Excel 與 Google 試算表都認。
 * 代價是用程式讀這個 CSV 的人會拿到帶 `="` 的字串 —— 這份檔案是給人開 Excel 用的，
 * 若之後要餵給程式，正解是另外開一條 API，不是把這裡改回去。
 */
const asText = (v) => `="${v}"`

/**
 * 把後端的 series 攤平成下載用的長表。
 *
 * 一列 = 一個案場的一個時間點，四個指標**永遠都給**（圖表的開關只管畫圖，
 * 不管檔案內容 —— 資料都抓回來了，少寫進去沒省到成本，卻要使用者重按一次）。
 * 值維持原始精度，四捨五入是顯示層的事。
 *
 * @param {object[][]} series 與送出的案場列同索引；缺資料的案場是空陣列，自然不產生列
 * @returns {object[]} download-csv 吃的物件陣列，key 就是 CSV 表頭
 */
const buildDownloadRows = (series) =>
  (series ?? []).flatMap((siteRows) =>
    [...(siteRows ?? [])]
      // 時間 key 都是零填補的，直接比字串就是時序
      .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0))
      .map((row) => {
        const out = { 案場: row.siteName, 時間: asText(row.time) }
        // null 交給 download-csv 會被寫成字串 "null"，統一換成空格
        METRICS.forEach((m) => {
          out[m.label] = row[m.key] ?? ''
        })
        return out
      })
  )

/** 下載一堆檔案回去要分得出來是哪個粒度、哪段區間，所以檔名帶齊 */
const downloadFileName = (grain, beginDate, endDate) =>
  grain === 'hour'
    ? `跨案場分析_時_${beginDate}`
    : `跨案場分析_${GRAIN_LABEL[grain]}_${beginDate}_${endDate}`

export { METRICS, metricOption, asText, buildDownloadRows, downloadFileName }
